/* eslint-disable */
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { C } from "../shared/globals.js";

const FM = "Montserrat,sans-serif";

const PERIODS = [
  { id:"1M",  label:"1M",  days:30  },
  { id:"3M",  label:"3M",  days:90  },
  { id:"6M",  label:"6M",  days:180 },
  { id:"1Y",  label:"1Y",  days:365 },
  { id:"ALL", label:"ALL", days:null },
];

const fmt$  = v => "$" + v.toLocaleString("en-US", { minimumFractionDigits:0, maximumFractionDigits:0 });
const fmtShares = v => {
  if (v >= 1000) return v.toLocaleString("en-US", { maximumFractionDigits:1 });
  if (v < 0.001) return v.toFixed(6);
  if (v < 1)     return v.toFixed(4);
  return v.toFixed(2);
};

function buildSeries(entries, cutoffDate) {
  const allSorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));
  let cumDollars = 0, cumShares = 0;
  const byDate = {};
  allSorted.forEach(e => {
    cumDollars += e.dollars || 0;
    cumShares  += e.shares  || 0;
    byDate[e.date] = { cumDollars, cumShares };
  });
  return Object.entries(byDate)
    .filter(([d]) => !cutoffDate || d >= cutoffDate)
    .sort(([a],[b]) => a.localeCompare(b))
    .map(([date, v]) => ({ date, ...v }));
}

export default function DCAChart({ assetData, COLORS }) {
  const [period,   setPeriod]   = useState("ALL");
  const [hidden,   setHidden]   = useState(new Set()); // tickers hidden
  const [tooltip,  setTooltip]  = useState(null);
  const wrapRef    = useRef(null);
  const tooltipRef = useRef(null);
  const [chartW,   setChartW]   = useState(600);

  useEffect(() => {
    const obs = new ResizeObserver(es => setChartW(es[0].contentRect.width || 600));
    if (wrapRef.current) obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  const assets = assetData.filter(a => a.entries?.length > 0);
  if (!assets.length) return null;

  const cutoffDate = (() => {
    const sel = PERIODS.find(p => p.id === period);
    if (!sel?.days) return null;
    const d = new Date();
    d.setDate(d.getDate() - sel.days);
    return d.toISOString().split("T")[0];
  })();

  const seriesMap = {};
  assets.forEach(a => { seriesMap[a.ticker] = buildSeries(a.entries, cutoffDate); });

  const allDates = [...new Set(
    Object.values(seriesMap).flatMap(s => s.map(p => p.date))
  )].sort();

  if (!allDates.length) return (
    <div style={{ textAlign:"center", padding:40, fontFamily:FM, fontSize:11, color:"var(--text4)" }}>No data for this period</div>
  );

  const PAD = { t:16, r:16, b:36, l:64 };
  const H   = 280;
  const gW  = chartW - PAD.l - PAD.r;
  const gH  = H - PAD.t - PAD.b;

  // Per-asset cumulative filled forward
  const assetByDate = {};
  assets.forEach(a => {
    assetByDate[a.ticker] = {};
    seriesMap[a.ticker].forEach(p => { assetByDate[a.ticker][p.date] = p; });
  });

  const filledAssets = assets.map(a => {
    let last = { cumDollars:0, cumShares:0 };
    return allDates.map(d => {
      if (assetByDate[a.ticker][d]) last = assetByDate[a.ticker][d];
      return { ...last };
    });
  });

  // Total line (only visible assets)
  const visibleAssets = assets.filter(a => !hidden.has(a.ticker));
  const visibleFilledAssets = filledAssets.filter((_, i) => !hidden.has(assets[i].ticker));

  const totalCumDollars = allDates.map((_, di) =>
    visibleFilledAssets.reduce((s, fs) => s + (fs[di]?.cumDollars || 0), 0)
  );

  const maxVal = Math.max(...filledAssets.flatMap(fs => fs.map(p => p.cumDollars)), 1);
  const toX    = idx => PAD.l + (idx / Math.max(allDates.length - 1, 1)) * gW;
  const toY    = v   => PAD.t + gH - (v / maxVal) * gH;

  const nLines  = 4;
  const gridVals = Array.from({ length: nLines + 1 }, (_, i) => (maxVal / nLines) * i);

  const labelDates = allDates.length <= 1
    ? allDates
    : [allDates[0], allDates[Math.floor(allDates.length/2)], allDates[allDates.length-1]];

  return (
    <div>
      {/* Period selector */}
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
        <div style={{ display:"flex", gap:4 }}>
          {PERIODS.map(p => (
            <button key={p.id} onClick={() => setPeriod(p.id)} style={{
              background: period === p.id ? `${C.orange}18` : "var(--bg3)",
              border:`1px solid ${period === p.id ? C.orange : "var(--border)"}`,
              borderRadius:6, padding:"4px 10px", cursor:"pointer",
              fontFamily:FM, fontSize:9, fontWeight:700,
              color: period === p.id ? C.orange : "var(--text4)",
              transition:"all 0.15s",
            }}>{p.label}</button>
          ))}
        </div>
      </div>

      {/* Legend — clickable */}
      <div style={{ display:"flex", flexWrap:"wrap", gap:10, marginBottom:14 }}>
        {assets.map((asset, ai) => {
          const color   = COLORS[ai % COLORS.length];
          const isHid   = hidden.has(asset.ticker);
          return (
            <button key={asset.ticker}
              onClick={() => setHidden(prev => {
                const next = new Set(prev);
                next.has(asset.ticker) ? next.delete(asset.ticker) : next.add(asset.ticker);
                return next;
              })}
              style={{
                display:"flex", alignItems:"center", gap:6,
                background: isHid ? "transparent" : `${color}12`,
                border:`1px solid ${isHid ? "var(--border)" : color}`,
                borderRadius:20, padding:"4px 10px", cursor:"pointer",
                opacity: isHid ? 0.4 : 1, transition:"all 0.15s",
              }}>
              <div style={{ width:8, height:8, borderRadius:"50%", background: isHid ? "#555" : color, flexShrink:0 }}/>
              <span style={{ fontFamily:FM, fontSize:10, fontWeight:700, color: isHid ? "var(--text4)" : color }}>
                {asset.ticker}
              </span>
              {!isHid && asset.livePrice > 0 && asset.avgPrice > 0 && (() => {
                const eff = ((asset.livePrice - asset.avgPrice) / asset.avgPrice) * 100;
                return (
                  <span style={{ fontFamily:FM, fontSize:9, fontWeight:700, color: eff >= 0 ? C.green : C.red }}>
                    {eff >= 0 ? "+" : ""}{eff.toFixed(1)}%
                  </span>
                );
              })()}
            </button>
          );
        })}
      </div>

      {/* Chart */}
      <div ref={wrapRef} style={{ position:"relative" }}>
        <svg width={chartW} height={H} style={{ display:"block", overflow:"visible" }}>

          {/* Grid */}
          {gridVals.map((v, i) => (
            <g key={i}>
              <line x1={PAD.l} y1={toY(v)} x2={PAD.l + gW} y2={toY(v)}
                stroke="var(--border)" strokeWidth="1" strokeDasharray="3 4"/>
              <text x={PAD.l - 6} y={toY(v) + 4} textAnchor="end"
                fill="var(--text4)" fontSize="8" fontFamily={FM} fontWeight="700">
                {v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : v >= 1000 ? `$${(v/1000).toFixed(0)}k` : `$${v.toFixed(0)}`}
              </text>
            </g>
          ))}

          {/* X labels */}
          {labelDates.map(d => (
            <text key={d} x={toX(allDates.indexOf(d))} y={H - 6}
              textAnchor="middle" fill="var(--text4)" fontSize="8" fontFamily={FM} fontWeight="600">
              {new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric" })}
            </text>
          ))}

          {/* Per-asset lines — solid, full color */}
          {assets.map((asset, ai) => {
            if (hidden.has(asset.ticker)) return null;
            const color = COLORS[ai % COLORS.length];
            const fs    = filledAssets[ai];
            const pts   = allDates.map((d, i) => `${toX(i)},${toY(fs[i]?.cumDollars || 0)}`).join(" ");
            // Find last nonzero point for dot
            const lastIdx = [...allDates].reduceRight((found, d, i) => found !== -1 ? found : (fs[i]?.cumDollars > 0 ? i : -1), -1);
            return (
              <g key={asset.ticker}>
                <polyline points={pts} fill="none" stroke={color} strokeWidth="2.5"
                  strokeLinejoin="round" strokeLinecap="round"/>
                {lastIdx >= 0 && (
                  <circle cx={toX(lastIdx)} cy={toY(fs[lastIdx].cumDollars)} r="4"
                    fill={color}/>
                )}
              </g>
            );
          })}

          {/* Hover overlay */}
          <rect x={PAD.l} y={PAD.t} width={gW} height={gH} fill="transparent"
            onMouseMove={e => {
              const rect = e.currentTarget.closest("svg").getBoundingClientRect();
              const relX = e.clientX - rect.left - PAD.l;
              const idx  = Math.max(0, Math.min(allDates.length-1, Math.round((relX/gW)*(allDates.length-1))));
              setTooltip({ date: allDates[idx], dateIdx: idx });
              const el = tooltipRef.current;
              if (el) {
                const TW = 210;
                const nearRight = e.clientX + 18 + TW > window.innerWidth;
                el.style.left = (nearRight ? e.clientX - TW - 10 : e.clientX + 14) + "px";
                el.style.top  = (e.clientY - 12) + "px";
              }
            }}
            onMouseLeave={() => setTooltip(null)}
          />

          {/* Hover line */}
          {tooltip && (() => {
            const idx = allDates.indexOf(tooltip.date);
            return idx >= 0 ? <line x1={toX(idx)} y1={PAD.t} x2={toX(idx)} y2={PAD.t+gH}
              stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="3 3"/> : null;
          })()}
        </svg>
      </div>

      {/* Tooltip */}
      {tooltip && createPortal(
        <div ref={tooltipRef} style={{ position:"fixed", zIndex:9999, pointerEvents:"none", whiteSpace:"nowrap", top:0, left:0 }}>
          <div style={{ background:"#1a1a1a", border:`1px solid ${C.orange}`, borderRadius:8, padding:"10px 14px", fontFamily:FM, boxShadow:"0 8px 24px rgba(0,0,0,0.9)", minWidth:200 }}>
            <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:"#a0a0a0", marginBottom:8 }}>
              {new Date(tooltip.date).toLocaleDateString("en-US", { weekday:"short", month:"short", day:"numeric", year:"numeric" })}
            </div>
            {/* Total */}
            {visibleAssets.length > 0 && (
              <div style={{ marginBottom:8 }}>
                <div style={{ fontSize:8, fontWeight:700, letterSpacing:"0.15em", color:"#666", marginBottom:2 }}>TOTAL INVESTED</div>
                <div style={{ fontSize:15, fontWeight:700, color:C.orange }}>{fmt$(totalCumDollars[tooltip.dateIdx] || 0)}</div>
              </div>
            )}
            <div style={{ width:"100%", height:1, background:"#2c2c2c", marginBottom:8 }}/>
            {/* Per asset */}
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {assets.map((asset, ai) => {
                if (hidden.has(asset.ticker)) return null;
                const color = COLORS[ai % COLORS.length];
                const fs    = filledAssets[ai];
                const pt    = fs[tooltip.dateIdx];
                if (!pt || pt.cumDollars === 0) return null;
                return (
                  <div key={asset.ticker}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                        <div style={{ width:6, height:6, borderRadius:"50%", background:color }}/>
                        <span style={{ fontSize:11, fontWeight:800, color:"#fff" }}>{asset.ticker}</span>
                      </div>
                      <span style={{ fontSize:10, fontWeight:700, color }}>{fmt$(pt.cumDollars)}</span>
                    </div>
                    <div style={{ paddingLeft:11, marginTop:1 }}>
                      <span style={{ fontSize:9, color:"#888" }}>{fmtShares(pt.cumShares)} shares</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
